// Typisierter API-Client. Standard: relativ zur App ("api/v1"), damit die App
// auf dem Webspace auch in einem Unterordner läuft (API unter <app>/api/).
export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'api/v1';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details: string[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Wird bei 401 aufgerufen (App zeigt dann die Anmeldung). */
let beiAbmeldung: (() => void) | null = null;
export function setzeAbmeldeHandler(f: (() => void) | null) {
  beiAbmeldung = f;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const methode = init?.method ?? 'GET';
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(methode !== 'GET' ? { 'X-Umzugsplaner': '1' } : {}),
      ...init?.headers,
    },
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok && response.status !== 503) {
    const b = (body ?? {}) as { fehler?: unknown; details?: unknown };
    const fehler =
      typeof b.fehler === 'string' ? b.fehler : `Serverfehler (HTTP ${response.status}).`;
    const details = Array.isArray(b.details) ? b.details.map(String) : [];
    if (response.status === 401 && !path.startsWith('/auth/login')) beiAbmeldung?.();
    throw new ApiError(response.status, fehler, details);
  }
  return body as T;
}

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  body: body === undefined ? undefined : JSON.stringify(body),
  headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
});

// ---------- Typen ----------

export type LonLat = [number, number];
export type Status = 'rot' | 'gelb' | 'gruen' | 'polizei' | 'grau';
export type Massnahme = 'vollsperrung' | 'teilsperrung' | 'kreisverkehr' | 'polizei';

export interface Health {
  status: 'ok' | 'eingeschraenkt';
  version: string;
  datenbank: 'ok' | 'nicht erreichbar';
  zeit: string;
}

export interface Nutzer {
  id: string;
  name: string;
  email: string;
  rolle: 'planer' | 'leser';
  organisation: { id: string; name: string };
}

export interface Veranstaltung {
  id: string;
  titel: string;
  art: string;
  datum: string;
  startzeit: string;
  teilnehmer: number;
  besonderheiten: string[];
  status: string;
  planversion_id: string | null;
  anordnung_status?: 'fehlt' | 'entwurf' | 'freigegeben' | 'korrektur';
  schritt?: 'vorgaben' | 'route' | 'kraefte' | 'ausgeben';
  analyse?: { rot: number; gelb_offen: number; vollstaendig: boolean } | null;
}

export interface Eckdaten {
  titel: string;
  art: string;
  startzeit: string;
  teilnehmer: number;
  besonderheiten: string[];
}

export interface LineString {
  type: 'LineString';
  coordinates: LonLat[];
}

export interface ZugParameter {
  geh_kmh: number | null;
  personen_pro_reihe: number | null;
  reihenabstand_m: number | null;
}

export interface RouteFeature {
  type: 'Feature';
  geometry: LineString;
  properties: { laenge_m: number; aktualisiert?: string; parameter?: ZugParameter };
}

export interface KreuzenderWeg {
  osm_id: number;
  name: string | null;
  highway: string;
  service: string | null;
  maxspeed: string | null;
  lanes: string | null;
  oneway: string | null;
  lit: string | null;
}

export interface Kreuzung {
  id: string;
  art: 'kandidat' | 'hinweis';
  bezeichnung: string;
  position: { type: 'Point'; coordinates: LonLat };
  station_m: number;
  abstand_m: number;
  osm_knoten: number[];
  wege: KreuzenderWeg[];
  ampel: boolean;
  bahnuebergang: boolean;
  kreisverkehr: boolean;
}

export interface KreuzungenAntwort {
  kreuzungen: Kreuzung[];
  anzahl_kandidaten: number;
  anzahl_hinweise: number;
  hinweis: string;
}

export interface Anordnung {
  id: string;
  veranstaltung_id: string;
  behoerde: string | null;
  aktenzeichen: string | null;
  datum: string | null;
  pdf: boolean;
  erfasst_von: { id: string; name: string } | null;
  freigegeben_von: { id: string; name: string } | null;
  freigegeben_am: string | null;
  korrektur: { grund: string; fundstelle: string; am: string } | null;
  status: 'entwurf' | 'freigegeben' | 'korrektur';
}

export interface Sperrstelle {
  id: string;
  kennung: string | null;
  bezeichnung: string | null;
  herkunft: 'angeordnet' | 'manuell' | 'erkannt';
  massnahme: Massnahme | null;
  sperrzeit_von: string | null;
  sperrzeit_bis: string | null;
  aufheben_nach_durchzug: boolean;
  fundstelle: string | null;
  quelle: 'manuell' | 'ki';
  position: { type: 'Point'; coordinates: LonLat };
}

export interface SperrstelleEingabe {
  kennung: string | null;
  bezeichnung: string | null;
  massnahme: Massnahme;
  sperrzeit_von: string | null;
  sperrzeit_bis: string | null;
  aufheben_nach_durchzug: boolean;
  fundstelle: string;
  position: { type: 'Point'; coordinates: LonLat };
}

export interface Auflage {
  id: string;
  text: string;
  fundstelle: string | null;
  pruefart: 'manuell' | 'regel';
  regel_key: string | null;
  parameter: Record<string, unknown> | null;
  erledigt: boolean;
  verantwortlich: string | null;
}

export interface AnordnungPaket {
  anordnung: Anordnung | null;
  sperrstellen: Sperrstelle[];
  auflagen: Auflage[];
  protokoll: { aktion: string; text: string | null; von: string | null; am: string }[];
}

export interface Befund {
  status: Status;
  code: string;
  begruendung: string;
  schluessel: string;
  bestaetigbar: boolean;
  bestaetigt: boolean;
}

export interface Bedarf {
  personen: number;
  fahrzeug: boolean;
  material: Record<string, number>;
  begruendung: string;
}

export interface Stelle {
  id: string;
  herkunft: 'angeordnet' | 'manuell' | 'erkannt';
  kennung: string | null;
  bezeichnung: string;
  massnahme: Massnahme | null;
  fundstelle: string | null;
  position: { type: 'Point'; coordinates: LonLat };
  station_m: number | null;
  kreuzungen: string[];
  wege: KreuzenderWeg[];
  sperrzeit_von: string | null;
  sperrzeit_bis: string | null;
  aufheben_nach_durchzug: boolean;
  zeiten: { ankunft: string; durchzug: string; besetzen: string; freigabe: string } | null;
  bedarf: Bedarf | null;
  vorschlag: 'teilnehmer_sichern' | null;
  personen_geplant: number;
  status: Status;
  befunde: Befund[];
}

export interface Analyse {
  zug: {
    start: string;
    ende: string;
    laenge_m: number;
    route_laenge_m: number;
    geh_kmh: number;
    personen_pro_reihe: number;
    reihenabstand_m: number;
    vorlauf_min: number;
    nachlauf_min: number;
  };
  stellen: Stelle[];
  hinweise: (Kreuzung & { status: 'grau'; begruendung: string })[];
  auflagen: {
    id: string;
    text: string;
    fundstelle: string | null;
    erledigt: boolean;
    status: Status;
    befund: Befund;
  }[];
  befunde_global: Befund[];
  summen: {
    personal_mindestens: number;
    personal_mit_wegen: number;
    personen_geplant: number;
    fahrzeuge: number;
    material_bedarf: Record<string, number>;
    material_bestand: Record<string, number>;
  };
  kraefte_vorschlag: Record<string, { von_stelle: string | null; personen: number }[]>;
  zaehler: { rot: number; gelb_offen: number };
  vollstaendig: boolean;
  erstellt_am?: string;
}

export interface Einheit {
  id: string;
  name: string;
  funkrufname: string | null;
  staerke: number;
  fahrzeug: string | null;
}

export interface Einsatzabschnitt {
  id: string;
  name: string;
  leiter: string | null;
  funkrufname: string | null;
  gespraechsgruppe: string | null;
}

export interface Posten {
  id: string;
  sperrstelle_id: string;
  einheit_id: string | null;
  einsatzabschnitt_id: string | null;
  personen: number;
  auftrag: string | null;
  material: Record<string, number>;
  besetzen_von: string | null;
  freigabe_ca: string | null;
}

export interface PostenEingabe {
  einheit_id: string | null;
  einsatzabschnitt_id: string | null;
  personen: number;
  auftrag: string | null;
  material?: Record<string, number>;
}

export interface ExportPaket {
  hinweis: string;
  attribution: string;
  erstellt: string;
  organisation: string | null;
  veranstaltung: Veranstaltung;
  anordnung: {
    behoerde: string | null;
    aktenzeichen: string | null;
    datum: string | null;
    status: string;
    freigegeben_am: string | null;
    freigegeben_von: string | null;
    auflagen: Auflage[];
  } | null;
  route: RouteFeature | null;
  analyse: Analyse | null;
  posten: Posten[];
  einheiten: Einheit[];
  einsatzabschnitte: Einsatzabschnitt[];
  geteilt?: boolean;
}

export interface NutzerEintrag {
  id: string;
  name: string;
  email: string;
  rolle: 'planer' | 'leser';
  aktiv: boolean;
}

// ---------- Aufrufe ----------

export const api = {
  health: () => request<Health>('/health'),

  anmelden: (email: string, passwort: string) =>
    request<{ nutzer: Nutzer }>('/auth/login', json('POST', { email, passwort })),
  abmelden: () => request<{ ok: boolean }>('/auth/logout', json('POST')),
  ich: () => request<{ nutzer: Nutzer }>('/auth/ich'),

  veranstaltungen: () => request<{ veranstaltungen: Veranstaltung[] }>('/veranstaltungen'),
  veranstaltung: (id: string) =>
    request<{ veranstaltung: Veranstaltung }>(`/veranstaltungen/${id}`),
  veranstaltungAnlegen: (d: Eckdaten) =>
    request<{ veranstaltung: Veranstaltung }>('/veranstaltungen', json('POST', d)),
  veranstaltungAendern: (id: string, d: Eckdaten) =>
    request<{ veranstaltung: Veranstaltung }>(`/veranstaltungen/${id}`, json('PUT', d)),
  veranstaltungLoeschen: (id: string) =>
    request<{ ok: boolean }>(`/veranstaltungen/${id}`, json('DELETE')),
  veranstaltungKopieren: (id: string, titel: string, startzeit: string) =>
    request<{ veranstaltung: Veranstaltung }>(
      `/veranstaltungen/${id}/kopieren`,
      json('POST', { titel, startzeit }),
    ),

  anordnung: (vid: string) => request<AnordnungPaket>(`/veranstaltungen/${vid}/anordnung`),
  anordnungAnlegen: (
    vid: string,
    d: { behoerde: string | null; aktenzeichen: string | null; datum: string | null },
  ) => request<AnordnungPaket>(`/veranstaltungen/${vid}/anordnung`, json('POST', d)),
  anordnungAendern: (
    id: string,
    d: { behoerde: string | null; aktenzeichen: string | null; datum: string | null },
  ) => request<AnordnungPaket>(`/anordnungen/${id}`, json('PUT', d)),
  pdfHochladen: (id: string, datei: File) => {
    const fd = new FormData();
    fd.append('pdf', datei);
    return request<AnordnungPaket>(`/anordnungen/${id}/pdf`, { method: 'POST', body: fd });
  },
  pdfUrl: (id: string) => `${API_BASE}/anordnungen/${id}/pdf`,
  sperrstelleAnlegen: (aid: string, d: SperrstelleEingabe) =>
    request<AnordnungPaket & { id: string }>(`/anordnungen/${aid}/sperrstellen`, json('POST', d)),
  sperrstelleAendern: (id: string, d: SperrstelleEingabe) =>
    request<AnordnungPaket>(`/sperrstellen/${id}`, json('PUT', d)),
  sperrstelleLoeschen: (id: string) =>
    request<AnordnungPaket>(`/sperrstellen/${id}`, json('DELETE')),
  auflageAnlegen: (aid: string, d: Record<string, unknown>) =>
    request<AnordnungPaket>(`/anordnungen/${aid}/auflagen`, json('POST', d)),
  auflageAendern: (id: string, d: Record<string, unknown>) =>
    request<AnordnungPaket>(`/auflagen/${id}`, json('PUT', d)),
  auflageLoeschen: (id: string) => request<AnordnungPaket>(`/auflagen/${id}`, json('DELETE')),
  freigeben: (aid: string) => request<AnordnungPaket>(`/anordnungen/${aid}/freigabe`, json('POST')),
  korrigieren: (aid: string, grund: string, fundstelle: string) =>
    request<AnordnungPaket>(`/anordnungen/${aid}/korrektur`, json('POST', { grund, fundstelle })),

  route: (pv: string) => request<{ route: RouteFeature | null }>(`/planversionen/${pv}/route`),
  routeSpeichern: (pv: string, coordinates: LonLat[], parameter?: ZugParameter) =>
    request<{ route: RouteFeature }>(`/planversionen/${pv}/route`, {
      ...json('PUT', {
        geometrie: { type: 'LineString', coordinates },
        ...(parameter ? { parameter } : {}),
      }),
    }),
  kreuzungen: (pv: string) => request<KreuzungenAntwort>(`/planversionen/${pv}/kreuzungen`),

  analyse: (pv: string) => request<{ analyse: Analyse | null }>(`/planversionen/${pv}/analyse`),
  analysieren: (pv: string) =>
    request<{ analyse: Analyse }>(`/planversionen/${pv}/analyse`, json('POST')),
  bestaetigen: (pv: string, schluessel: string) =>
    request<{ ok: boolean }>(`/planversionen/${pv}/bestaetigungen`, json('POST', { schluessel })),
  bestaetigungAufheben: (pv: string, schluessel: string) =>
    request<{ ok: boolean }>(`/planversionen/${pv}/bestaetigungen`, json('DELETE', { schluessel })),

  einheiten: () => request<{ einheiten: Einheit[] }>('/einheiten'),
  einheitAnlegen: (d: Omit<Einheit, 'id'>) =>
    request<{ einheiten: Einheit[] }>('/einheiten', json('POST', d)),
  einheitAendern: (id: string, d: Omit<Einheit, 'id'>) =>
    request<{ einheiten: Einheit[] }>(`/einheiten/${id}`, json('PUT', d)),
  einheitLoeschen: (id: string) =>
    request<{ einheiten: Einheit[] }>(`/einheiten/${id}`, json('DELETE')),
  ressourcen: () => request<{ ressourcen: Record<string, number> }>('/ressourcen'),
  ressourcenSetzen: (r: Record<string, number>) =>
    request<{ ressourcen: Record<string, number> }>('/ressourcen', json('PUT', { ressourcen: r })),
  abschnitte: (pv: string) =>
    request<{ einsatzabschnitte: Einsatzabschnitt[] }>(`/planversionen/${pv}/einsatzabschnitte`),
  abschnittAnlegen: (pv: string, d: Omit<Einsatzabschnitt, 'id'>) =>
    request<{ einsatzabschnitte: Einsatzabschnitt[] }>(
      `/planversionen/${pv}/einsatzabschnitte`,
      json('POST', d),
    ),
  abschnittAendern: (id: string, d: Omit<Einsatzabschnitt, 'id'>) =>
    request<{ ok: boolean }>(`/einsatzabschnitte/${id}`, json('PUT', d)),
  abschnittLoeschen: (id: string) =>
    request<{ ok: boolean }>(`/einsatzabschnitte/${id}`, json('DELETE')),
  posten: (pv: string) => request<{ posten: Posten[] }>(`/planversionen/${pv}/posten`),
  postenAnlegen: (d: PostenEingabe & { sperrstelle_id: string }) =>
    request<{ id: string; posten: Posten[] }>('/posten', json('POST', d)),
  postenAendern: (id: string, d: PostenEingabe) =>
    request<{ posten: Posten[] }>(`/posten/${id}`, json('PUT', d)),
  postenLoeschen: (id: string) => request<{ posten: Posten[] }>(`/posten/${id}`, json('DELETE')),
  vorschlagUebernehmen: (pv: string, sperrstelleId: string) =>
    request<{ posten: Posten[] }>(
      `/planversionen/${pv}/vorschlag-uebernehmen`,
      json('POST', { sperrstelle_id: sperrstelleId }),
    ),

  regelwerk: () =>
    request<{ regelwerk: { version: number; inhalt: Record<string, unknown> } | null }>(
      '/regelwerk',
    ),
  regelwerkSpeichern: (inhalt: unknown) =>
    request<{ version: number }>('/regelwerk', json('PUT', { inhalt })),
  nutzer: () => request<{ nutzer: NutzerEintrag[] }>('/nutzer'),
  nutzerAnlegen: (d: { name: string; email: string; rolle: string; passwort: string }) =>
    request<{ nutzer: NutzerEintrag[] }>('/nutzer', json('POST', d)),
  nutzerAendern: (id: string, d: Partial<{ rolle: string; aktiv: boolean; passwort: string }>) =>
    request<{ nutzer: NutzerEintrag[] }>(`/nutzer/${id}`, json('PUT', d)),

  teilenStatus: (pv: string) => request<{ token: string | null }>(`/planversionen/${pv}/teilen`),
  teilen: (pv: string) => request<{ token: string }>(`/planversionen/${pv}/teilen`, json('POST')),
  teilenBeenden: (pv: string) =>
    request<{ token: null }>(`/planversionen/${pv}/teilen`, json('DELETE')),
  export: (pv: string) => request<ExportPaket>(`/planversionen/${pv}/export`),
  geteilt: (token: string) => request<ExportPaket>(`/geteilt/${token}`),
};
